/**
 * canvas.tsx — the renderer + editor for the typed Business Operating Domain component.
 *
 * One component, two lenses over a {@link BusinessModel}:
 *   • value-proposition — the zoomed Customer Profile ↔ Value Map for one segment, with
 *     the fit drawn as live links (hover a pain → its relievers light up, and back).
 *   • model — the nine-area Business Model Canvas, every entry colour-coded by the
 *     customer segment(s) it serves, filterable down to one segment's subset.
 *
 * Cross-references are by entry `id` within the model (a reliever `addresses` a pain id),
 * so the fit links and the colour mapping are pure derivations — no duplicated content.
 * In edit mode entries are click-to-fill cards (the same `gb-inp` typography as the rest
 * of the studio); `open` blanks invite a fill, `candidate` entries carry accept/dismiss.
 */
import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import type {
  BusinessCanvasBlock,
  BusinessModel,
  CanvasEntry,
  CustomerSegment,
  ModelAreaKey,
  ValueProposition,
} from "../../domain/business.ts";
import { AREA_GLYPH, AREA_LABEL, SEGMENT_TONE_COUNT, entryId } from "../../domain/business.ts";
import { AutoTextarea } from "../lib/auto-textarea.tsx";

export interface BusinessCanvasProps {
  readonly block: BusinessCanvasBlock;
  readonly onChange?: ((block: BusinessCanvasBlock) => void) | undefined;
  readonly readOnly?: boolean | undefined;
}

/** A CSS custom-property bag, typed for the `style` prop (which has no string index). */
function vars(o: Record<string, string>): CSSProperties {
  return o as CSSProperties;
}

// ── Derivations shared by both lenses ────────────────────────────────────────────
interface Links {
  /** entry id → ids it addresses (forward) ∪ ids that address it (reverse). */
  readonly related: ReadonlyMap<string, ReadonlySet<string>>;
}

function collectEntries(model: BusinessModel): CanvasEntry[] {
  const out: CanvasEntry[] = [];
  for (const s of model.segments) out.push(...s.jobs, ...s.pains, ...s.gains);
  for (const v of model.valuePropositions) out.push(...v.products, ...v.painRelievers, ...v.gainCreators);
  out.push(
    ...model.channels,
    ...model.customerRelationships,
    ...model.revenueStreams,
    ...model.keyResources,
    ...model.keyActivities,
    ...model.keyPartnerships,
    ...model.costStructure,
  );
  return out;
}

function buildLinks(model: BusinessModel): Links {
  const related = new Map<string, Set<string>>();
  const edge = (a: string, b: string): void => {
    (related.get(a) ?? related.set(a, new Set()).get(a)!).add(b);
    (related.get(b) ?? related.set(b, new Set()).get(b)!).add(a);
  };
  for (const e of collectEntries(model)) {
    for (const t of e.addresses ?? []) edge(e.id, t);
  }
  return { related };
}

function toneOf(seg: CustomerSegment, index: number): number {
  return (((seg.tone ?? index) % SEGMENT_TONE_COUNT) + SEGMENT_TONE_COUNT) % SEGMENT_TONE_COUNT;
}

// ── A single entry card (read + click-to-edit) ───────────────────────────────────
interface EntryCardProps {
  readonly entry: CanvasEntry;
  readonly tones: readonly number[];
  readonly readOnly: boolean | undefined;
  readonly hover: string | null;
  readonly related: ReadonlySet<string> | undefined;
  readonly onHover: (id: string | null) => void;
  readonly onChange: (next: CanvasEntry) => void;
  readonly onRemove: () => void;
}

function EntryCard({ entry, tones, readOnly, hover, related, onHover, onChange, onRemove }: EntryCardProps): JSX.Element {
  const [editing, setEditing] = useState(entry.state === "open" && !readOnly);
  // Linked = the hovered entry is in THIS entry's related set (the other end of a fit link).
  const isLinked = hover != null && hover !== entry.id && (related?.has(hover) ?? false);
  const isHovered = hover === entry.id;
  const dimmed = hover != null && !isHovered && !isLinked;
  const cls = [
    "boc-entry",
    entry.state === "candidate" ? "is-candidate" : "",
    entry.state === "open" ? "is-open" : "",
    isHovered ? "is-hovered" : "",
    isLinked ? "is-linked" : "",
    dimmed ? "is-dimmed" : "",
  ].filter(Boolean).join(" ");
  // The segment accent stripe(s) — one ribbon, split if the entry serves several segments.
  const stripe: CSSProperties | undefined = tones.length > 0
    ? {
        background: tones.length === 1
          ? `var(--seg-${tones[0]})`
          : `linear-gradient(180deg, ${tones.map((t, i) => `var(--seg-${t}) ${(i / tones.length) * 100}% ${((i + 1) / tones.length) * 100}%`).join(",")})`,
      }
    : undefined;

  const grade = entry.grade ?? 0;
  const setGrade = (g: number): void => onChange({ ...entry, grade: g === grade ? 0 : g });

  return (
    <div className={cls} onMouseEnter={() => onHover(entry.id)} onMouseLeave={() => onHover(null)}>
      {stripe ? <span className="boc-stripe" style={stripe} aria-hidden="true" /> : null}
      <div className="boc-entry-main">
        {editing && !readOnly ? (
          <AutoTextarea
            rows={1}
            autoFocus
            className="gb-inp boc-inp"
            value={entry.text}
            placeholder="…"
            onChange={(e) => onChange({ ...entry, text: e.target.value, ...(entry.state === "open" ? { state: "filled" as const } : {}) })}
            onBlur={() => setEditing(false)}
          />
        ) : (
          <button type="button" className="boc-entry-text" disabled={readOnly} onClick={() => !readOnly && setEditing(true)} title={readOnly ? undefined : "click to edit"}>
            {entry.text || <span className="boc-placeholder">add…</span>}
          </button>
        )}
      </div>

      {entry.state === "candidate" && !readOnly ? (
        <div className="boc-cand-ctl" title="AI candidate — accept or dismiss">
          <button type="button" className="boc-mini ok" aria-label="accept" onClick={() => onChange({ ...entry, state: "filled" })}>✓</button>
          <button type="button" className="boc-mini danger" aria-label="dismiss" onClick={onRemove}>✕</button>
        </div>
      ) : !readOnly ? (
        <div className="boc-entry-ctl">
          <div className="boc-grade" role="group" aria-label="priority">
            {[1, 2, 3, 4, 5].map((g) => (
              <button key={g} type="button" className={`boc-pip ${g <= grade ? "on" : ""}`} aria-label={`grade ${g}`} onClick={() => setGrade(g)} />
            ))}
          </div>
          <button type="button" className="boc-mini danger" aria-label="remove" onClick={onRemove}>✕</button>
        </div>
      ) : grade > 0 ? (
        <div className="boc-grade ro" aria-label={`priority ${grade} of 5`}>
          {[1, 2, 3, 4, 5].map((g) => <span key={g} className={`boc-pip ${g <= grade ? "on" : ""}`} />)}
        </div>
      ) : null}
    </div>
  );
}

// ── A labelled list of entries (one sub-area) ────────────────────────────────────
interface EntryListProps {
  readonly label: string;
  readonly glyph?: string | undefined;
  readonly entries: readonly CanvasEntry[];
  readonly tonesFor: (e: CanvasEntry) => readonly number[];
  readonly links: Links;
  readonly hover: string | null;
  readonly onHover: (id: string | null) => void;
  readonly readOnly: boolean | undefined;
  readonly onChange: (entries: CanvasEntry[]) => void;
  readonly idPrefix: string;
  readonly accent?: number | undefined;
}

function EntryList({ label, glyph, entries, tonesFor, links, hover, onHover, readOnly, onChange, idPrefix, accent }: EntryListProps): JSX.Element {
  const set = (i: number, next: CanvasEntry): void => onChange(entries.map((e, j) => (j === i ? next : e)));
  const remove = (i: number): void => onChange(entries.filter((_, j) => j !== i));
  const add = (): void => onChange([...entries, { id: entryId(idPrefix, label, entries.length + 1), text: "", state: "open" }]);
  return (
    <div className="boc-list" style={accent != null ? vars({ "--accent": `var(--seg-${accent})` }) : undefined}>
      <div className="boc-list-head">
        {glyph ? <span className="boc-list-glyph" aria-hidden="true">{glyph}</span> : null}
        <span className="boc-list-label">{label}</span>
        <span className="boc-list-count">{entries.length}</span>
      </div>
      <div className="boc-list-body">
        {entries.map((e, i) => (
          <EntryCard
            key={e.id}
            entry={e}
            tones={tonesFor(e)}
            readOnly={readOnly}
            hover={hover}
            related={links.related.get(e.id)}
            onHover={onHover}
            onChange={(n) => set(i, n)}
            onRemove={() => remove(i)}
          />
        ))}
        {!readOnly ? (
          <button type="button" className="boc-add" onClick={add}>＋ {label.toLowerCase()}</button>
        ) : entries.length === 0 ? (
          <div className="boc-empty">—</div>
        ) : null}
      </div>
    </div>
  );
}

// ── Lens 1: the Value Proposition Canvas (one segment ↔ its value proposition) ───
function ValuePropositionLens({ model, segment, segIndex, links, hover, onHover, readOnly, update }: {
  model: BusinessModel;
  segment: CustomerSegment | undefined;
  segIndex: number;
  links: Links;
  hover: string | null;
  onHover: (id: string | null) => void;
  readOnly: boolean | undefined;
  update: (next: BusinessModel) => void;
}): JSX.Element {
  if (!segment) {
    return <div className="boc-empty-state">No customer segment yet — add one to shape its value proposition.</div>;
  }
  const tone = toneOf(segment, segIndex);
  const vpIndex = model.valuePropositions.findIndex((v) => v.segmentIds.includes(segment.id));
  const vp = vpIndex >= 0 ? model.valuePropositions[vpIndex] : undefined;

  const setSeg = (patch: Partial<CustomerSegment>): void =>
    update({ ...model, segments: model.segments.map((s) => (s.id === segment.id ? { ...s, ...patch } : s)) });
  const setVp = (patch: Partial<ValueProposition>): void => {
    if (vp) update({ ...model, valuePropositions: model.valuePropositions.map((v, j) => (j === vpIndex ? { ...v, ...patch } : v)) });
  };
  const tones = (): readonly number[] => [tone];
  const accentStyle = vars({ "--accent": `var(--seg-${tone})` });

  return (
    <div className="vpc">
      <section className="vpc-side vpc-map" style={accentStyle}>
        <header className="vpc-side-head"><span className="vpc-shape sq" aria-hidden="true">▢</span><span>Value Map</span>{vp ? <span className="vpc-side-name">{vp.name}</span> : null}</header>
        {vp ? (
          <>
            <EntryList label="Products & Services" glyph={AREA_GLYPH.valuePropositions} entries={vp.products} tonesFor={tones} links={links} hover={hover} onHover={onHover} readOnly={readOnly} idPrefix="pr" onChange={(products) => setVp({ products })} />
            <EntryList label="Pain Relievers" glyph="✚" entries={vp.painRelievers} tonesFor={tones} links={links} hover={hover} onHover={onHover} readOnly={readOnly} idPrefix="rel" onChange={(painRelievers) => setVp({ painRelievers })} />
            <EntryList label="Gain Creators" glyph="↗" entries={vp.gainCreators} tonesFor={tones} links={links} hover={hover} onHover={onHover} readOnly={readOnly} idPrefix="gc" onChange={(gainCreators) => setVp({ gainCreators })} />
          </>
        ) : (
          <div className="boc-empty-state">
            No value proposition serves this segment yet.
            {!readOnly ? (
              <button type="button" className="boc-add" onClick={() => update({ ...model, valuePropositions: [...model.valuePropositions, { id: entryId("vp", segment.name, model.valuePropositions.length + 1), name: `For ${segment.name}`, segmentIds: [segment.id], products: [], painRelievers: [], gainCreators: [] }] })}>＋ value proposition</button>
            ) : null}
          </div>
        )}
      </section>

      <div className="vpc-fit" aria-hidden="true"><span className="vpc-fit-glyph">⟺</span><span className="vpc-fit-label">fit</span></div>

      <section className="vpc-side vpc-profile" style={accentStyle}>
        <header className="vpc-side-head"><span className="vpc-shape ci" aria-hidden="true">◯</span><span>Customer Profile</span><span className="vpc-side-name">{segment.name}</span></header>
        {readOnly ? (segment.profile ? <p className="vpc-profile-desc">{segment.profile}</p> : null) : (
          <AutoTextarea rows={1} className="gb-inp vpc-profile-desc" placeholder="who is this segment? (optional)" value={segment.profile ?? ""} onChange={(e) => setSeg({ profile: e.target.value })} />
        )}
        <EntryList label="Customer Jobs" glyph="◷" entries={segment.jobs} tonesFor={tones} links={links} hover={hover} onHover={onHover} readOnly={readOnly} idPrefix="job" onChange={(jobs) => setSeg({ jobs })} />
        <EntryList label="Pains" glyph="▽" entries={segment.pains} tonesFor={tones} links={links} hover={hover} onHover={onHover} readOnly={readOnly} idPrefix="pain" onChange={(pains) => setSeg({ pains })} />
        <EntryList label="Gains" glyph="△" entries={segment.gains} tonesFor={tones} links={links} hover={hover} onHover={onHover} readOnly={readOnly} idPrefix="gain" onChange={(gains) => setSeg({ gains })} />
      </section>
    </div>
  );
}

// ── Lens 2: the full Business Model Canvas (nine areas) ──────────────────────────
function BusinessModelLens({ model, toneByseg, filter, links, hover, onHover, readOnly, update }: {
  model: BusinessModel;
  toneByseg: ReadonlyMap<string, number>;
  filter: string | null;
  links: Links;
  hover: string | null;
  onHover: (id: string | null) => void;
  readOnly: boolean | undefined;
  update: (next: BusinessModel) => void;
}): JSX.Element {
  const tonesFor = (e: CanvasEntry): readonly number[] =>
    (e.segmentIds ?? []).map((id) => toneByseg.get(id)).filter((t): t is number => t != null);
  const visible = (entries: readonly CanvasEntry[]): CanvasEntry[] =>
    filter == null ? [...entries] : entries.filter((e) => !e.segmentIds || e.segmentIds.length === 0 || e.segmentIds.includes(filter));
  const area = (key: ModelAreaKey): JSX.Element => (
    <EntryList
      label={AREA_LABEL[key] ?? key}
      glyph={AREA_GLYPH[key]}
      entries={visible(model[key])}
      tonesFor={tonesFor}
      links={links}
      hover={hover}
      onHover={onHover}
      readOnly={readOnly}
      idPrefix={key.slice(0, 3)}
      onChange={(next) => update({ ...model, [key]: next })}
    />
  );

  return (
    <div className="bmc">
      <div className="bmc-cell a-kp">{area("keyPartnerships")}</div>
      <div className="bmc-cell a-ka">{area("keyActivities")}</div>
      <div className="bmc-cell a-vp"><ValuePropsCell model={model} toneByseg={toneByseg} filter={filter} links={links} hover={hover} onHover={onHover} readOnly={readOnly} update={update} /></div>
      <div className="bmc-cell a-cr">{area("customerRelationships")}</div>
      <div className="bmc-cell a-cs"><SegmentsCell model={model} toneByseg={toneByseg} filter={filter} readOnly={readOnly} update={update} /></div>
      <div className="bmc-cell a-kr">{area("keyResources")}</div>
      <div className="bmc-cell a-ch">{area("channels")}</div>
      <div className="bmc-cell a-co">{area("costStructure")}</div>
      <div className="bmc-cell a-re">{area("revenueStreams")}</div>
    </div>
  );
}

// Value Propositions cell: each VP is a small card listing its served segments (colour pips).
function ValuePropsCell({ model, toneByseg, filter, links, hover, onHover, readOnly, update }: {
  model: BusinessModel;
  toneByseg: ReadonlyMap<string, number>;
  filter: string | null;
  links: Links;
  hover: string | null;
  onHover: (id: string | null) => void;
  readOnly: boolean | undefined;
  update: (next: BusinessModel) => void;
}): JSX.Element {
  const vps = filter == null ? model.valuePropositions : model.valuePropositions.filter((v) => v.segmentIds.includes(filter));
  const setVp = (id: string, patch: Partial<ValueProposition>): void =>
    update({ ...model, valuePropositions: model.valuePropositions.map((v) => (v.id === id ? { ...v, ...patch } : v)) });
  return (
    <div className="boc-list">
      <div className="boc-list-head"><span className="boc-list-glyph" aria-hidden="true">{AREA_GLYPH.valuePropositions}</span><span className="boc-list-label">{AREA_LABEL.valuePropositions}</span><span className="boc-list-count">{vps.length}</span></div>
      <div className="boc-list-body">
        {vps.map((v) => (
          <div className="boc-vp" key={v.id}>
            <div className="boc-vp-name">
              {readOnly ? v.name : <AutoTextarea rows={1} className="gb-inp boc-inp strong" value={v.name} placeholder="value proposition" onChange={(e) => setVp(v.id, { name: e.target.value })} />}
            </div>
            <SegmentToggle model={model} toneByseg={toneByseg} selected={v.segmentIds} readOnly={readOnly} onToggle={(segId) => setVp(v.id, { segmentIds: v.segmentIds.includes(segId) ? v.segmentIds.filter((s) => s !== segId) : [...v.segmentIds, segId] })} />
            <ul className="boc-vp-items">
              {[...v.products, ...v.painRelievers, ...v.gainCreators].slice(0, 4).map((e) => (
                <li key={e.id} className={hover != null && (links.related.get(e.id)?.has(hover) || hover === e.id) ? "lit" : ""} onMouseEnter={() => onHover(e.id)} onMouseLeave={() => onHover(null)}>{e.text}</li>
              ))}
            </ul>
          </div>
        ))}
        {!readOnly ? <button type="button" className="boc-add" onClick={() => update({ ...model, valuePropositions: [...model.valuePropositions, { id: entryId("vp", "prop", model.valuePropositions.length + 1), name: "", segmentIds: filter ? [filter] : [], products: [], painRelievers: [], gainCreators: [] }] })}>＋ value proposition</button> : vps.length === 0 ? <div className="boc-empty">—</div> : null}
      </div>
    </div>
  );
}

// Customer Segments cell: each segment is a colour-keyed card; selecting one filters the canvas.
function SegmentsCell({ model, toneByseg, filter, readOnly, update }: {
  model: BusinessModel;
  toneByseg: ReadonlyMap<string, number>;
  filter: string | null;
  readOnly: boolean | undefined;
  update: (next: BusinessModel) => void;
}): JSX.Element {
  const setSeg = (id: string, patch: Partial<CustomerSegment>): void =>
    update({ ...model, segments: model.segments.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const addSeg = (): void => {
    const tone = model.segments.length % SEGMENT_TONE_COUNT;
    update({ ...model, segments: [...model.segments, { id: entryId("seg", "segment", model.segments.length + 1), name: "", tone, jobs: [], pains: [], gains: [] }] });
  };
  return (
    <div className="boc-list">
      <div className="boc-list-head"><span className="boc-list-glyph" aria-hidden="true">{AREA_GLYPH.segments}</span><span className="boc-list-label">{AREA_LABEL.segments}</span><span className="boc-list-count">{model.segments.length}</span></div>
      <div className="boc-list-body">
        {model.segments.map((s) => {
          const tone = toneByseg.get(s.id) ?? 0;
          const dim = filter != null && filter !== s.id;
          return (
            <div key={s.id} className={`boc-seg ${dim ? "is-dimmed" : ""}`} style={vars({ "--accent": `var(--seg-${tone})` })}>
              <span className="boc-seg-dot" aria-hidden="true" />
              {readOnly ? <span className="boc-seg-name">{s.name}</span> : <AutoTextarea rows={1} className="gb-inp boc-inp strong" value={s.name} placeholder="segment / ICP" onChange={(e) => setSeg(s.id, { name: e.target.value })} />}
              <span className="boc-seg-meta">{s.jobs.length}j · {s.pains.length}p · {s.gains.length}g</span>
            </div>
          );
        })}
        {!readOnly ? <button type="button" className="boc-add" onClick={addSeg}>＋ segment</button> : model.segments.length === 0 ? <div className="boc-empty">—</div> : null}
      </div>
    </div>
  );
}

// A row of segment colour chips; toggles membership (edit) or just shows it (read).
function SegmentToggle({ model, toneByseg, selected, readOnly, onToggle }: {
  model: BusinessModel;
  toneByseg: ReadonlyMap<string, number>;
  selected: readonly string[];
  readOnly: boolean | undefined;
  onToggle: (segId: string) => void;
}): JSX.Element {
  return (
    <div className="boc-segchips">
      {model.segments.map((s) => {
        const on = selected.includes(s.id);
        const tone = toneByseg.get(s.id) ?? 0;
        return (
          <button key={s.id} type="button" className={`boc-segchip ${on ? "on" : ""}`} style={vars({ "--accent": `var(--seg-${tone})` })} disabled={readOnly} title={s.name} onClick={() => !readOnly && onToggle(s.id)}>
            <span className="boc-segchip-dot" aria-hidden="true" />
            {s.name || "segment"}
          </button>
        );
      })}
    </div>
  );
}

// ── The shell ────────────────────────────────────────────────────────────────────
export function BusinessCanvas({ block, onChange, readOnly }: BusinessCanvasProps): JSX.Element {
  const model = block.model;
  const [view, setView] = useState<"model" | "value-proposition">(block.view ?? "model");
  const [filter, setFilter] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);

  const links = useMemo(() => buildLinks(model), [model]);
  const toneByseg = useMemo(() => {
    const m = new Map<string, number>();
    model.segments.forEach((s, i) => m.set(s.id, toneOf(s, i)));
    return m;
  }, [model]);

  const update = (next: BusinessModel): void => onChange?.({ ...block, model: next });

  const activeSegId = filter ?? model.segments[0]?.id ?? null;
  const activeSeg = model.segments.find((s) => s.id === activeSegId);
  const activeSegIndex = model.segments.findIndex((s) => s.id === activeSegId);

  return (
    <div className={`boc ${readOnly ? "ro" : ""}`}>
      <header className="boc-head">
        <div className="boc-head-title">
          <span className="boc-kicker">Business Operating Domain</span>
          {readOnly ? <h3 className="boc-title">{model.title ?? block.title ?? "Business Canvas"}</h3>
            : <AutoTextarea rows={1} className="gb-inp boc-title-inp" value={model.title ?? ""} placeholder="canvas title" onChange={(e) => update({ ...model, title: e.target.value })} />}
        </div>
        <div className="boc-lens" role="tablist" aria-label="canvas lens">
          <button role="tab" aria-selected={view === "model"} className={`boc-lens-btn ${view === "model" ? "on" : ""}`} onClick={() => setView("model")}>Business Model</button>
          <button role="tab" aria-selected={view === "value-proposition"} className={`boc-lens-btn ${view === "value-proposition" ? "on" : ""}`} onClick={() => setView("value-proposition")}>Value Proposition</button>
        </div>
      </header>

      {model.segments.length > 0 ? (
        <div className="boc-legend">
          <span className="boc-legend-lbl">{view === "model" ? "view" : "segment"}</span>
          {view === "model" ? (
            <button type="button" className={`boc-legend-chip all ${filter == null ? "on" : ""}`} onClick={() => setFilter(null)}>Whole business</button>
          ) : null}
          {model.segments.map((s, i) => {
            const tone = toneOf(s, i);
            const on = view === "model" ? filter === s.id : activeSegId === s.id;
            return (
              <button key={s.id} type="button" className={`boc-legend-chip ${on ? "on" : ""}`} style={vars({ "--accent": `var(--seg-${tone})` })} onClick={() => setFilter(view === "model" && filter === s.id ? null : s.id)}>
                <span className="boc-legend-dot" aria-hidden="true" />
                {s.name || `Segment ${i + 1}`}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="boc-body">
        {view === "value-proposition" ? (
          <ValuePropositionLens model={model} segment={activeSeg} segIndex={activeSegIndex < 0 ? 0 : activeSegIndex} links={links} hover={hover} onHover={setHover} readOnly={readOnly} update={update} />
        ) : (
          <BusinessModelLens model={model} toneByseg={toneByseg} filter={filter} links={links} hover={hover} onHover={setHover} readOnly={readOnly} update={update} />
        )}
      </div>
    </div>
  );
}
